/**
 * Asset Management Design System - Colors
 * Extracted from Figma: Asset Management - UI
 * Generated: 2026-01-30
 *
 * Color naming convention:
 * - primary-100 to primary-1000 (light to dark)
 * - neutral-base-white, neutral-bg-light, neutral-bg-light-colored, neutral-100 to neutral-base-black
 * - success-100 to success-500
 * - warning-100 to warning-500
 * - error-100 to error-500
 */

export const colors = {
	// Primary Colors
	primary: {
		'100': '#F3F7FF',
		'200': '#D3E2FD',
		'300': '#A6C6FD',
		'400': '#688EF9',
		'500': '#2373F6',
		'600': '#1A60D2',
		'700': '#124BA8',
		'800': '#0E3A85',
		'900': '#072962',
		'1000': '#042252',
	},

	// Neutrals Colors
	neutrals: {
		'base-white': '#FFFFFF',
		'bg-light-colored': '#F5F7FB',
		'bg-active': '#E9EDF5',
		outline: '#E5E5E7',
		'gray-disabled': '#D4D5D9',
		'gray-light': '#EEF0F3',
		'gray-medium': '#9FA0A9',
		'gray-dark': '#373941',
		'dark-base': '#000002',
	},

	success: {
		'100': '#E6F4ED',
		'200': '#BBDFCA',
		'300': '#90CAA8',
		'400': '#4CA976',
		'500': '#1F9553',
	},

	warning: {
		'100': '#FCF3E8',
		'200': '#F2DABE',
		'300': '#EAC094',
		'400': '#E09C54',
		'500': '#D8832C',
	},

	error: {
		'100': '#FBEAE9',
		'200': '#F2C3BF',
		'300': '#EA9D94',
		'400': '#E06455',
		'500': '#D73C2B',
	},
} as const;

export type PrimaryColor = keyof typeof colors.primary;
export type NeutralColor = keyof typeof colors.neutrals;
export type SuccessColor = keyof typeof colors.success;
export type WarningColor = keyof typeof colors.warning;
export type ErrorColor = keyof typeof colors.error;

// Helper function to get color value
export function getColor(category: 'primary' | 'neutrals' | 'success' | 'warning' | 'error', shade: string): string {
	const categoryColors = colors[category];
	return (categoryColors as Record<string, string>)[shade] || '';
}

// Semantic color aliases for common use cases
export const semanticColors = {
	// Primary
	primaryMain: colors.primary['500'],
	primaryLight: colors.primary['100'],
	primaryDark: colors.primary['900'],

	textPrimary: colors.neutrals['dark-base'],
	textSecondary: colors.neutrals['gray-medium'],
	textDisabled: colors.neutrals['gray-disabled'],

	backgroundDefault: colors.neutrals['base-white'],
	backgroundPaper: colors.neutrals['base-white'],
	backgroundLight: colors.neutrals['bg-light-colored'],
	backgroundColored: colors.neutrals['bg-active'],

	success: colors.success['500'],
	successLight: colors.success['100'],
	warning: colors.warning['500'],
	warningLight: colors.warning['100'],
	error: colors.error['500'],
	errorLight: colors.error['100'],
} as const;
